//codex-- Keep the inspections toolbar layout and draft filters scoped to this page.
import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { CalendarDaysIcon, ClipboardDocumentListIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

//codex-- Use explicit status labels and shared sizing throughout the toolbar.
const STATUS_OPTIONS = [
  ['all', 'All statuses'], ['invoiced', 'Invoiced'], ['notInvoiced', 'Not invoiced'],
  ['pending', 'Pending'], ['complete', 'Complete'], ['ready', 'Ready, not invoiced'], ['followup', 'Follow up'],
];
const EMPTY_FILTERS = { billing: 'all', search: '', tech: '', date_from: '', date_to: '' };
const FIELD_CLASS = 'block h-11 w-full min-w-0 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20';
//codex-- Restore the inspection button colors with lighter fills and readable dark labels/icons.
const LINK_CLASS = 'inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2';
const BUTTON_COLORS = {
  yellow: 'border-yellow-200 bg-yellow-100 text-yellow-900 hover:bg-yellow-200 focus-visible:outline-yellow-600',
  orange: 'border-orange-200 bg-orange-100 text-orange-900 hover:bg-orange-200 focus-visible:outline-orange-600',
  green: 'border-green-200 bg-green-100 text-green-900 hover:bg-green-200 focus-visible:outline-green-600',
  blue: 'border-blue-200 bg-blue-100 text-blue-900 hover:bg-blue-200 focus-visible:outline-blue-600',
};

//codex-- Stage every filter locally until one Apply action sends the complete query.
export default function InspectionActionBar({ query, onQueryChange, employees, loading }) {
  const [draft, setDraft] = useState(() => Object.fromEntries(
    Object.keys(EMPTY_FILTERS).map((key) => [key, query[key] ?? EMPTY_FILTERS[key]])
  ));
  const dateError = Boolean(draft.date_from) !== Boolean(draft.date_to)
    ? 'Choose both dates, or leave both empty.'
    : draft.date_from && draft.date_from > draft.date_to
      ? 'The end date must be on or after the start date.' : '';
  const hasChanges = Object.keys(EMPTY_FILTERS).some((key) =>
    (key === 'search' ? draft[key].trim() : draft[key]) !== query[key]
  );
  const updateDraft = (key, value) => setDraft((previous) => ({ ...previous, [key]: value }));

  //codex-- Allow status/search/technician-only filtering; validate dates only when used.
  const applyFilters = (event) => {
    event.preventDefault();
    if (dateError || loading) return;
    const filters = { ...draft, search: draft.search.trim() };
    setDraft(filters);
    onQueryChange({ ...filters, page: 1 });
  };

  //codex-- Clear filters locally and keep the selected month without reloading the app.
  const clearFilters = () => {
    setDraft({ ...EMPTY_FILTERS });
    onQueryChange({ ...EMPTY_FILTERS, page: 1 });
  };

  //codex-- Removing an applied filter updates that filter and preserves other drafts.
  const removeFilter = (changes) => {
    setDraft((previous) => ({ ...previous, ...changes }));
    onQueryChange({ ...changes, page: 1 });
  };

  //codex-- Describe only applied filters so pending edits never mislabel the results.
  const technician = employees.find((employee) => String(employee.id) === String(query.tech) ||
    `${employee.first_name} ${employee.last_name}` === query.tech);
  const activeFilters = [
    query.billing !== 'all' && {
      key: 'status', label: STATUS_OPTIONS.find(([value]) => value === query.billing)?.[1] || query.billing,
      clear: { billing: 'all' },
    },
    query.tech && {
      key: 'technician', label: technician ? `${technician.first_name} ${technician.last_name}` : query.tech,
      clear: { tech: '' },
    },
    query.date_from && query.date_to && {
      key: 'date range', label: `${query.date_from} to ${query.date_to}`, clear: { date_from: '', date_to: '' },
    },
    query.search && { key: 'search', label: `Search: ${query.search}`, clear: { search: '' } },
  ].filter(Boolean);
  const selectedTechnician = employees.find((employee) =>
    `${employee.first_name} ${employee.last_name}` === draft.tech);

  return (
    //codex-- Place navigation above the responsive filter grid and keep every link visible on mobile.
    <section aria-label="Inspection tools and filters" className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/80 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <p className="text-sm font-semibold text-slate-800">Inspection tools</p>
        {/*codex-- Keep scheduler yellow, contracts orange, and inspection calendar green in soft shades. */}
        <nav aria-label="Inspection tools" className="flex flex-wrap gap-2">
          <NavLink to="/full_scheduler_viewer" className={`${LINK_CLASS} ${BUTTON_COLORS.yellow}`}>
            <CalendarDaysIcon aria-hidden="true" className="h-4 w-4 shrink-0" />Full scheduler
          </NavLink>
          <NavLink to="/maintenance" className={`${LINK_CLASS} ${BUTTON_COLORS.orange}`}>
            <ClipboardDocumentListIcon aria-hidden="true" className="h-4 w-4 shrink-0" />Contracts
          </NavLink>
          <NavLink to="/inspection_calendar" className={`${LINK_CLASS} ${BUTTON_COLORS.green}`}>
            <CalendarDaysIcon aria-hidden="true" className="h-4 w-4 shrink-0" />Inspection calendar
          </NavLink>
        </nav>
      </div>

      {/*codex-- Label each field and give search its own space rather than overlaying buttons. */}
      <form onSubmit={applyFilters} className="p-4 sm:p-5">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-12">
          <div className="col-span-2 lg:col-span-4">
            <label htmlFor="inspection-search" className="mb-1.5 block text-xs font-semibold text-slate-600">Search inspections</label>
            <div className="relative">
              <MagnifyingGlassIcon aria-hidden="true" className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
              <input id="inspection-search" type="search" value={draft.search}
                onChange={(event) => updateDraft('search', event.target.value)}
                placeholder="ID, customer, site, or contract type" className={`${FIELD_CLASS} pl-9`} />
            </div>
          </div>
          <div className="col-span-2 sm:col-span-1 lg:col-span-2">
            <label htmlFor="inspection-status" className="mb-1.5 block text-xs font-semibold text-slate-600">Inspection / billing status</label>
            <select id="inspection-status" className={FIELD_CLASS} value={draft.billing}
              onChange={(event) => updateDraft('billing', event.target.value)}>
              {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div className="col-span-2 sm:col-span-1 lg:col-span-2">
            <label htmlFor="inspection-technician" className="mb-1.5 block text-xs font-semibold text-slate-600">Technician</label>
            <select id="inspection-technician" className={FIELD_CLASS}
              value={selectedTechnician ? String(selectedTechnician.id) : draft.tech}
              onChange={(event) => updateDraft('tech', event.target.value)}>
              <option value="">All technicians</option>
              {employees.map((employee) => (
                <option key={employee.id} value={String(employee.id)}>{employee.first_name} {employee.last_name}</option>
              ))}
            </select>
          </div>
          <div className="col-span-1 lg:col-span-2">
            <label htmlFor="inspection-from" className="mb-1.5 block text-xs font-semibold text-slate-600">From</label>
            <input id="inspection-from" type="date" className={FIELD_CLASS} value={draft.date_from}
              aria-invalid={Boolean(dateError)} aria-describedby={dateError ? 'inspection-date-error' : undefined}
              onChange={(event) => updateDraft('date_from', event.target.value)} />
          </div>
          <div className="col-span-1 lg:col-span-2">
            <label htmlFor="inspection-to" className="mb-1.5 block text-xs font-semibold text-slate-600">To</label>
            <input id="inspection-to" type="date" className={FIELD_CLASS} value={draft.date_to}
              aria-invalid={Boolean(dateError)} aria-describedby={dateError ? 'inspection-date-error' : undefined}
              onChange={(event) => updateDraft('date_to', event.target.value)} />
          </div>
        </div>

        {/*codex-- Keep validation inline and use one submit action, including Enter from the search field. */}
        {dateError && <p id="inspection-date-error" role="alert" className="mt-2 text-sm text-red-600">{dateError}</p>}
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs text-slate-500" role="status">
            {hasChanges ? 'Unapplied changes' : 'Use any combination of filters.'}
          </span>
          {/*codex-- Match the service toolbar's lighter orange Clear and original blue Filter palette. */}
          <div className="grid grid-cols-2 gap-2 sm:flex">
            <button type="button" onClick={clearFilters}
              className={`inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border px-4 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${BUTTON_COLORS.orange}`}>
              <XMarkIcon aria-hidden="true" className="h-4 w-4" />Clear filters
            </button>
            <button type="submit" disabled={loading || Boolean(dateError)}
              className={`inline-flex h-10 items-center justify-center rounded-lg border px-4 text-sm font-semibold shadow-sm disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${BUTTON_COLORS.blue}`}>
              {loading ? 'Loading…' : 'Apply filters'}
            </button>
          </div>
        </div>
      </form>

      {/*codex-- Applied-filter labels can be removed individually without losing the selected month. */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-4 py-3 sm:px-5" aria-label="Applied filters">
          <span className="text-xs font-semibold text-slate-500">Applied</span>
          {activeFilters.map((filter) => (
            <button key={filter.key} type="button" aria-label={`Remove ${filter.key} filter`}
              onClick={() => removeFilter(filter.clear)}
              className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              <span className="max-w-[240px] truncate">{filter.label}</span><XMarkIcon aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
